import type { RuleInput } from "./feature-flags-api";
import type { TargetingRule } from "./feature-flags-types";
import { classifyServeRule, SIMPLE_SERVE_RULE_ID } from "./serve-rule";

/** The two calls this needs, injected so the decision can be tested without a network. */
export interface ServeValuePorts {
  createRule: (key: string, input: RuleInput) => Promise<TargetingRule>;
  updateRule: (
    key: string,
    ruleId: string,
    input: Omit<RuleInput, "ruleId">
  ) => Promise<TargetingRule>;
}

export interface SetServeValueInput {
  readonly flagKey: string;
  readonly rules: readonly TargetingRule[];
  readonly variationKey: string;
  readonly variationValue: unknown;
}

/**
 * Set what a flag serves while it is enabled, as one unconditional rule.
 *
 * Create or update is decided from the flag's CURRENT rules rather than from
 * anything the caller passes, so the console cannot be talked into adding a
 * second rule: two rules read as targeting, which would lock the operator out
 * of the control they had just used.
 *
 * Throws rather than writing when the flag carries real targeting. The form
 * behind this offers a variation key and a value; conditions, segments and
 * partial rollouts cannot be represented in it, and saving over them would
 * move users nobody decided to move.
 */
export async function setServeValue(
  input: SetServeValueInput,
  ports: ServeValuePorts
): Promise<void> {
  const state = classifyServeRule(input.rules);

  if (state.kind === "complex") {
    throw new Error(
      "This flag has targeting rules that cannot be edited here. Change them through the flags API."
    );
  }

  const body = {
    variationKey: input.variationKey,
    variationValue: input.variationValue,
    rolloutPct: 100,
    priority: 100,
  };

  if (state.kind === "inert") {
    await ports.createRule(input.flagKey, { ruleId: SIMPLE_SERVE_RULE_ID, ...body });
    return;
  }

  await ports.updateRule(input.flagKey, state.rule.ruleId, body);
}
